import React, { useCallback, useMemo } from "react";
import { Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { Connection, Integration } from "/app/src/models";
import MaxRecords from "./maxRecords";
import Retries from "./retries";
import { convertStringToBoolean, isAPIType } from "/app/src/helpers";
import Box from "/app/src/components/generic/components/box";
import {
  Form,
  SubmitButton,
  Checkbox,
  InputNumber,
  Select,
  Input,
} from "formik-antd";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import useMultipleSettings from "/app/src/hooks/useMultipleSettings";
import SelectWithSkeleton from "../../../../selectWithSkeleton";
import useConnectionSettings from "/app/src/hooks/useConnectionSettings";
import DynamicButtons from "./dynamicButtons";
import DynamicCascader from "./dynamicCascader";
import NegativePick from "./negativePick";

interface FormValues {
  connectionId: number | undefined;
  verify: boolean | undefined;
  timeout: string | undefined;
  path: string | undefined;
  maxRecords: string | undefined;
  retries: string | undefined;
  method: string | undefined;
}

/**
 * Component to display the connection settings for a Data Push Integration
 * @param integration Integration
 */
export default function ConnectionSettings({
  integration,
  pathToggle = true,
  verifyToggle = true,
  timeoutToggle = true,
  maxRecordsToggle = true,
  retriesToggle = true,
  connections,
  methodToggle = false,
  isFetchingConnections,
  negativePickToggle = false,
}: {
  integration: Integration;
  pathToggle?: boolean;
  verifyToggle?: boolean;
  timeoutToggle?: boolean;
  maxRecordsToggle?: boolean;
  retriesToggle?: boolean;
  connections: Connection[];
  methodToggle?: boolean;
  isFetchingConnections?: boolean;
  negativePickToggle?: boolean;
}) {
  const { t } = useTranslation();

  const {
    submitHandler,
    verify,
    timeout,
    path,
    maxRecords,
    retries,
    method,
    negativeQuantity,
  } = useMultipleSettings({
    integrationId: integration.id,
    settingTypes: [
      "verify",
      "timeout",
      "path",
      "maxRecords",
      "retries",
      "method",
      "negativeQuantity",
    ],
  });
  const { connectionSubmitHandler } = useConnectionSettings({
    integration,
  });

  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await submitHandler(values);
      await connectionSubmitHandler(values);
      actions.resetForm();
    },
    [submitHandler, connectionSubmitHandler],
  );

  const methodLabelContent = useMemo(
    () => (
      <>
        {t("translation:method")}
        <IconToolTip content={t("translation:method_tip")} className="ml-1" />
      </>
    ),
    [t],
  );

  const verifyLabelContent = useMemo(
    () => (
      <>
        {t("translation:verify")}
        <IconToolTip content={t("translation:verify_tip")} className="ml-1" />
      </>
    ),
    [t],
  );

  const renderOptions = useCallback(() => {
    return connections.map((c) => (
      <Select.Option key={c.id} value={c.id}>
        {c.name}
      </Select.Option>
    ));
  }, [connections]);

  const settingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting }) => (
        <Form layout="vertical">
          <div className="flex items-center justify-between">
            <h1>{t("translation:connections")}</h1>
            <SubmitButton
              disabled={!dirty || isSubmitting}
              loading={isSubmitting}
              type="primary"
              size="large"
            >
              {t("translation:save")}
            </SubmitButton>
          </div>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item
                name="connectionId"
                label={t("translation:connection")}
              >
                <SelectWithSkeleton
                  loading={isFetchingConnections}
                  placeholder={t("translation:select_connection")}
                  name="connectionId"
                  renderOptions={renderOptions}
                />
              </Form.Item>
            </Col>
            {integration.connectionType === "REST" && pathToggle && (
              <Col span={8}>
                <Form.Item
                  style={{ marginBottom: "0px" }}
                  name="path"
                  label={t("translation:path")}
                  hasFeedback={false}
                >
                  <Input.TextArea
                    name="path"
                    placeholder={t("translation:enter_path")}
                    size="large"
                    autoSize
                  />
                </Form.Item>
                {integration.appName === "Data Pull" && (
                  <DynamicButtons dataPull />
                )}
                {integration.appName === "Data Push" && (
                  <DynamicCascader integration={integration} />
                )}{" "}
              </Col>
            )}
            {isAPIType(integration.connectionType) && (
              <>
                {methodToggle && (
                  <Col span={8}>
                    <Form.Item
                      name="method"
                      label={methodLabelContent}
                      hasFeedback={false}
                    >
                      <Select name="method" size="large">
                        <Select.Option value="POST">
                          {t("translation:post")}
                        </Select.Option>
                        <Select.Option value="PATCH">
                          {t("translation:patch")}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                {timeoutToggle && (
                  <Col span={8}>
                    <Form.Item
                      name="timeout"
                      label={t("translation:timeout")}
                      hasFeedback={false}
                    >
                      <InputNumber
                        name="timeout"
                        min={1}
                        max={90}
                        size="large"
                        style={{ width: "100%" }}
                        placeholder="10"
                      />
                    </Form.Item>
                  </Col>
                )}
                {maxRecordsToggle && (
                  <Col span={8}>
                    <MaxRecords />
                  </Col>
                )}
                {negativePickToggle && (
                  <Col span={8}>
                    <NegativePick />
                  </Col>
                )}
                {retriesToggle && (
                  <Col span={8}>
                    <Retries />
                  </Col>
                )}
                {verifyToggle && (
                  <Col span={8}>
                    <Form.Item name="verify" label={verifyLabelContent}>
                      <Checkbox name="verify" />
                    </Form.Item>
                  </Col>
                )}
              </>
            )}
          </Row>
        </Form>
      ),
      [
        integration,
        isFetchingConnections,
        maxRecordsToggle,
        methodLabelContent,
        methodToggle,
        pathToggle,
        renderOptions,
        retriesToggle,
        t,
        timeoutToggle,
        verifyLabelContent,
        verifyToggle,
        negativePickToggle,
      ],
    );

  return (
    <Box>
      <Formik
        initialValues={{
          connectionId: integration.connectionId,
          verify: convertStringToBoolean(verify),
          timeout: timeout || "",
          path: path || "",
          maxRecords: maxRecords || "",
          retries: retries || "",
          method: method || "",
          negativeQuantity: convertStringToBoolean(negativeQuantity),
        }}
        onSubmit={onSubmit}
        enableReinitialize
        component={settingsForm}
      />
    </Box>
  );
}
